'use client';

import { CopyLinkIcon, FacebookIcon, LinkedInIcon, PinterestIcon, XIcon } from '@components/Icons';
import AnchorButton from '@components/shared/AnchorButton/AnchorButton';
import { BlogPostPageType } from '@lib/sanity/queries/blog/blogPost/types';
import { ButtonProjection, FullPortableTextProjection } from '@lib/sanity/types/common';
import { Button } from '@ui/Button/Button';
import Text, { TextVariant } from '@ui/Text/Text';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

const SHARE_URLS = {
  X: (url: string, title: string) =>
    `https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  LINKEDIN: (url: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  FACEBOOK: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  PINTEREST: (url: string, title: string) =>
    `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}`,
};

type BlogSidebarProps = {
  data: BlogPostPageType;
  ctaButton?: ButtonProjection;
  embedCode?: string | null;
};

type CategoryTagsProps = {
  categories?: Array<{
    title?: {
      value?: string | null;
    } | null;
    slug: string;
  }> | null;
  noLabel?: boolean;
};

type ShareArticleProps = {
  url: string;
  title: string;
};

export function CategoryTags({ categories, noLabel = false }: CategoryTagsProps) {
  if (!categories) {
    return null;
  }

  return (
    <div>
      {!noLabel && <Text as="h3" variant={TextVariant.TextUnicase} content="Category" className="mb-3" />}
      <div className="flex flex-wrap gap-2">
        {categories?.map((category, index) => {
          const title = category.title?.value || 'Uncategorized';
          const slug = category.slug;

          return (
            <Link key={index} href={`/blog/category/${slug}/`}>
              <Button variant="outline" size="xs">
                {title}
              </Button>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ShareArticle({ url, title }: ShareArticleProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openShare = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  return (
    <div>
      <Text as="h3" variant={TextVariant.TextUnicase} content="Share This Article" className="mb-3" />
      <div className="mt-6 flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <div onClick={() => openShare(SHARE_URLS.X(url, title))} className="hover:cursor-pointer">
            <XIcon />
          </div>
          <div onClick={() => openShare(SHARE_URLS.LINKEDIN(url))} className="hover:cursor-pointer">
            <LinkedInIcon />
          </div>
          <div onClick={() => openShare(SHARE_URLS.FACEBOOK(url))} className="hover:cursor-pointer">
            <FacebookIcon />
          </div>
          <div onClick={() => openShare(SHARE_URLS.PINTEREST(url, title))} className="hover:cursor-pointer">
            <PinterestIcon />
          </div>
          <div onClick={handleCopy} className="hover:cursor-pointer">
            <CopyLinkIcon />
          </div>
          {copied && (
            // eslint-disable-next-line @nx/workspace-no-hardcoded-strings
            <span className="text-secondary font-the-future text-sm">Copied to clipboard</span>
          )}
        </div>
      </div>
    </div>
  );
}

type Heading = {
  id: string;
  text: string;
  children?: Heading[];
};

type TableOfContentsProps = {
  body?: FullPortableTextProjection;
};

function TableOfContents({ body }: TableOfContentsProps) {
  const extractHeadings = (blocks?: FullPortableTextProjection): Heading[] => {
    if (!blocks) {
      return [];
    }
    const headings: Heading[] = [];

    blocks.value?.forEach((block) => {
      if (block?._type !== 'block' || !blocks.value?.length) {
        return;
      }
      if (block.style === 'h2' && block.children) {
        const h2Text = block.children.map((child) => child.text).join('');
        const h2Id = h2Text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

        const h2Heading: Heading = {
          id: h2Id,
          text: h2Text,
          children: [],
        };

        // Look for h3 headings that follow this h2
        let nextBlockIndex = blocks.value?.indexOf(block) + 1;
        while (nextBlockIndex < blocks.value?.length) {
          const nextBlock = blocks.value?.[nextBlockIndex];
          if (nextBlock?._type === 'block' && nextBlock.style === 'h2') {
            break; // Stop when we hit the next h2
          }

          if (nextBlock?._type === 'block' && nextBlock.style === 'h3' && nextBlock.children) {
            const h3Text = nextBlock.children.map((child) => child.text).join('');
            const h3Id = h3Text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
            h2Heading.children?.push({
              id: h3Id,
              text: h3Text,
            });
          }

          nextBlockIndex++;
        }

        headings.push(h2Heading);
      }
    });

    return headings;
  };

  const headings = extractHeadings(body);

  if (headings.length === 0) {
    return null;
  }

  return (
    <div>
      <Text as="h3" variant={TextVariant.TextUnicase} content="Page Content" className="mb-3" />
      <nav className="space-y-2">
        {headings.map((heading) => (
          <div key={heading.id}>
            <a href={`#${heading.id}`}>
              <Text variant={TextVariant.TextBodyMD} content={heading.text} />
            </a>
            {heading.children && heading.children.length > 0 && (
              <div className="ml-4 mt-1 space-y-1">
                {heading.children.map((child) => (
                  <a key={child.id} href={`#${child.id}`}>
                    <Text variant={TextVariant.TextBodyMD} content={child.text} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

type NewsletterSignupProps = {
  embedCode?: string | null;
};

const DEFAULT_FORM_MIN_HEIGHT = 419;

function NewsletterSignup({ embedCode }: NewsletterSignupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!embedCode || !containerRef.current) {
      return;
    }

    containerRef.current.innerHTML = '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = embedCode;

    // Extract min-height from embed code if present
    const wrapperDiv = tempDiv.querySelector('#hubspot-form-wrapper');
    const styleMatch = wrapperDiv?.getAttribute('style')?.match(/min-height:\s*(\d+)px/);
    const minHeight = styleMatch?.[1] || String(DEFAULT_FORM_MIN_HEIGHT);

    // Set min-height on wrapper immediately to prevent layout shift
    if (wrapperRef.current) {
      wrapperRef.current.style.minHeight = `${minHeight}px`;
    }

    // Handle scripts in order - external scripts first, then inline scripts
    const scripts = tempDiv.querySelectorAll('script');
    const externalScripts: HTMLScriptElement[] = [];
    const inlineScripts: HTMLScriptElement[] = [];

    // Separate external and inline scripts
    scripts.forEach((script) => {
      if (script.src) {
        externalScripts.push(script);
      } else {
        inlineScripts.push(script);
      }
    });

    // Load external scripts first
    const loadExternalScripts = async () => {
      for (const script of externalScripts) {
        const newScript = document.createElement('script');
        Array.from(script.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Wait for script to load

        await new Promise((resolve, reject) => {
          newScript.onload = resolve;
          newScript.onerror = reject;
          document.head.appendChild(newScript);
        });
      }
    };

    // Load inline scripts after external scripts are loaded
    const loadInlineScripts = () => {
      inlineScripts.forEach((script) => {
        const newScript = document.createElement('script');
        newScript.textContent = script.textContent;
        containerRef.current?.appendChild(newScript);
      });
    };

    loadExternalScripts()
      .then(() => {
        setTimeout(loadInlineScripts, 100);
      })
      .catch((error) => {
        console.error('Error loading external scripts:', error);
      });

    Array.from(tempDiv.children).forEach((child) => {
      if (child.tagName !== 'SCRIPT') {
        containerRef.current?.appendChild(child.cloneNode(true));
      }
    });

    // Adjust wrapper height after form loads if form height changes
    const adjustHeight = () => {
      if (wrapperRef.current && containerRef.current) {
        const formWrapper = containerRef.current.querySelector<HTMLElement>('#hubspot-form-wrapper');
        if (formWrapper && formWrapper.offsetHeight > parseInt(minHeight, 10)) {
          wrapperRef.current.style.minHeight = `${formWrapper.offsetHeight}px`;
        }
      }
    };

    // Check height after a delay to allow form to render
    const heightCheckInterval = setInterval(() => {
      adjustHeight();
    }, 500);

    // Clean up interval after 10 seconds (form should be loaded by then)
    const timeoutId = setTimeout(() => {
      clearInterval(heightCheckInterval);
      adjustHeight(); // Final adjustment
    }, 10000);

    // Cleanup function
    return () => {
      clearInterval(heightCheckInterval);
      clearTimeout(timeoutId);
    };
  }, [embedCode]);

  if (!embedCode) {
    return null;
  }

  return (
    <div>
      <Text as="h3" variant={TextVariant.TextUnicase} content="Newsletter Signup" className="mb-3" />
      <div ref={wrapperRef} style={{ minHeight: '419px' }}>
        <div ref={containerRef} />
      </div>
    </div>
  );
}

/**
 * Custom hook for bi-directional sticky behavior.
 * Sticks at top when scrolling up, sticks at bottom when scrolling down.
 * Allows seeing all sidebar content while scrolling through the page.
 */
function useBidirectionalSticky(topOffset = 80) {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [stickyStyle, setStickyStyle] = useState<React.CSSProperties>({
    position: 'sticky',
    top: topOffset,
  });
  const lastScrollY = useRef(0);
  const currentTop = useRef(topOffset);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const sidebarHeight = sidebar.offsetHeight;
      const scrollingDown = scrollY > lastScrollY.current;

      // If sidebar fits in viewport, just use regular sticky at top
      if (sidebarHeight <= viewportHeight - topOffset) {
        currentTop.current = topOffset;
        setStickyStyle({ position: 'sticky', top: topOffset });
        lastScrollY.current = scrollY;
        return;
      }

      const scrollDelta = scrollY - lastScrollY.current;

      if (scrollingDown) {
        // Scrolling down: move sidebar up until bottom is visible
        const minTop = viewportHeight - sidebarHeight;
        const newTop = Math.max(minTop, currentTop.current - scrollDelta);
        currentTop.current = newTop;
      } else {
        // Scrolling up: move sidebar down until top reaches offset
        const newTop = Math.min(topOffset, currentTop.current - scrollDelta);
        currentTop.current = newTop;
      }

      setStickyStyle({
        position: 'sticky',
        top: currentTop.current,
      });

      lastScrollY.current = scrollY;
    };

    // Initialize
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [topOffset]);

  return { sidebarRef, stickyStyle };
}

export default function BlogSidebar({ data, ctaButton, embedCode }: BlogSidebarProps) {
  const { sidebarRef, stickyStyle } = useBidirectionalSticky(80); // 80px = 5rem (top-20)

  return (
    <div ref={sidebarRef} style={stickyStyle} className="flex flex-col gap-8">
      <AnchorButton
        button={ctaButton ? { ...ctaButton, size: 'xl', variant: 'primary' } : undefined}
        buttonClassName="w-full sm:w-full"
      />
      <TableOfContents body={data.body} />
      <NewsletterSignup embedCode={embedCode} />
      <ShareArticle
        url={typeof window !== 'undefined' ? window.location.href : ''}
        title={data.title?.value || 'Blog Post'}
      />
      <CategoryTags categories={data.categories} />
    </div>
  );
}
